/**
 * Multiple-choice answers: labels, what gets submitted, and keyboard keys.
 *
 * An MC answer is submitted as text — the chosen value formatted in English —
 * so it goes through exactly the same parseAnswer → compare path as typed and
 * handwritten answers (docs/GAME_DESIGN.md § 1, "Input mode never changes game
 * logic"). The display label is localized (Afrikaans shows a decimal comma).
 */
import { eq, formatRational, parseAnswer, type AnswerKind, type Locale, type Rational } from '@dropstack/core';

export type ChoiceIndex = 0 | 1 | 2 | 3;

export const CHOICE_LETTERS = ['A', 'B', 'C', 'D'] as const;

export const CHOICE_INDICES: readonly ChoiceIndex[] = [0, 1, 2, 3];

/**
 * React key for choice button `i` — the same for every problem, on purpose:
 * the four buttons stay mounted from sum to sum and only their content
 * changes, so a keyboard, switch or screen-reader user's focus stays on the
 * button it was on (a key that included the problem id would replace the
 * buttons and drop focus to the page every sum).
 */
export function choiceKey(i: ChoiceIndex): string {
  return `choice-${i}`;
}

/** What the button shows, e.g. "−3/4", "0,75" (af) or "12". */
export function choiceLabel(value: Rational, kind: AnswerKind, locale: Locale): string {
  return formatRational(value, kind, locale);
}

/**
 * The text submitted for a choice: formatRational(value, kind, 'en'), which
 * parseAnswer reads back as the same value. If a display form would not
 * survive the round trip (e.g. a decimal with more places than parseAnswer
 * accepts), the exact fraction is sent instead, so picking the right button
 * can never be scored wrong.
 */
export function choiceSubmitValue(value: Rational, kind: AnswerKind): string {
  const text = formatRational(value, kind, 'en');
  const back = parseAnswer(text);
  return back !== null && eq(back, value) ? text : formatRational(value, 'fraction', 'en');
}

/** Keyboard shortcut → choice: 1–4 or A–D (either case). Null for any other key. */
export function choiceIndexForKey(key: string): ChoiceIndex | null {
  switch (key) {
    case '1':
    case 'a':
    case 'A':
      return 0;
    case '2':
    case 'b':
    case 'B':
      return 1;
    case '3':
    case 'c':
    case 'C':
      return 2;
    case '4':
    case 'd':
    case 'D':
      return 3;
    default:
      return null;
  }
}
